import type { PdfDocumentObjectV2 } from "@bezpart-board/shared";
import { importPdfToBoard, type ImportPdfOptions, type ImportPdfProgress } from "./importPdfToBoard";

export type ImportPresentationProgress =
  | ImportPdfProgress
  | { phase: "converting"; page: number; total: number };

const OFFICE_MIMES = new Set([
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-powerpoint",
]);

export function isPdfFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return file.type === "application/pdf" || name.endsWith(".pdf");
}

export function isOfficePresentationFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (name.endsWith(".pptx") || name.endsWith(".ppt")) return true;
  return OFFICE_MIMES.has(file.type);
}

export function isPresentationFile(file: File): boolean {
  return isPdfFile(file) || isOfficePresentationFile(file);
}

async function convertOfficeToPdfBlob(opts: ImportPdfOptions): Promise<File> {
  const form = new FormData();
  form.append("file", opts.file, opts.file.name);
  form.append("boardId", opts.boardId);
  if (opts.shareToken) form.append("shareToken", opts.shareToken);

  let url = "/api/v1/assets/convert-to-pdf";
  if (opts.shareToken) url += `?t=${encodeURIComponent(opts.shareToken)}`;
  else if (opts.guestEditKey) url += `?k=${encodeURIComponent(opts.guestEditKey)}`;

  const res = await fetch(url, {
    method: "POST",
    credentials: opts.useCredentials ? "include" : "omit",
    body: form,
  });

  if (!res.ok) {
    let message = "Не удалось конвертировать презентацию";
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const base = opts.file.name.replace(/\.(pptx|ppt)$/i, "") || "presentation";
  return new File([blob], `${base}.pdf`, { type: "application/pdf" });
}

export type ImportPresentationOptions = Omit<ImportPdfOptions, "onProgress"> & {
  onProgress?: (p: ImportPresentationProgress) => void;
};

/** PDF напрямую; PPTX/PPT — через серверную конвертацию, затем тот же PDF-пайплайн. */
export async function importPresentationToBoard(
  opts: ImportPresentationOptions
): Promise<PdfDocumentObjectV2> {
  if (isPdfFile(opts.file)) {
    return importPdfToBoard({
      ...opts,
      onProgress: opts.onProgress,
    });
  }
  if (!isOfficePresentationFile(opts.file)) {
    throw new Error("Поддерживаются PDF, PPTX и PPT");
  }
  // Настоящий потолок задаёт сервер (ASSET_MAX_OFFICE_BYTES); здесь тот же
  // по умолчанию, чтобы не гонять зря десятки мегабайт и получить отказ.
  if (opts.file.size > 52_428_800) {
    throw new Error("Презентация больше 50 МБ — уменьшите её или разбейте на части");
  }

  opts.onProgress?.({ phase: "converting", page: 0, total: 0 });
  const pdfFile = await convertOfficeToPdfBlob(opts);
  return importPdfToBoard({
    ...opts,
    file: pdfFile,
    onProgress: opts.onProgress,
  });
}

export function presentationProgressLabel(p: ImportPresentationProgress): string {
  if (p.phase === "converting") return "Конвертация презентации…";
  if (p.phase === "loading") return "Чтение PDF…";
  if (p.phase === "rendering") return `Рендер страницы ${p.page}/${p.total}…`;
  if (p.phase === "placing") return "Добавление на доску…";
  if (p.phase === "uploading") {
    if (p.total === 0) return "Загрузка PDF…";
    return `Загрузка страниц ${p.page}/${p.total}…`;
  }
  if (p.phase === "done") return "Готово…";
  return "Импорт…";
}
